import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import GoogleAuthButton from "@/components/GoogleAuthButton";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [params] = useSearchParams();
  const prefilledUrl = params.get("url") || "";

  const [form, setForm] = useState({
    business_name: "",
    website_url: prefilledUrl,
    email: "",
    password: "",
  });
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Account created.");
      navigate("/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="register-page">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-obsidian text-bone">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-3 h-3 bg-intl" />
          <span className="font-app font-bold">AI Visibility OS</span>
        </Link>
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-bone/60">// 3 fields. 60 seconds.</div>
          <h2 className="mt-4 font-marketing text-5xl tracking-tighter leading-none">
            Find out exactly how <span className="text-intl">AI sees you.</span>
          </h2>
          <ul className="mt-8 space-y-2 text-bone/70 font-mono text-sm">
            <li>→ Deterministic 0-100 visibility score</li>
            <li>→ Plain-language explanations</li>
            <li>→ Paste-ready fix code</li>
          </ul>
        </div>
        <div className="font-mono text-xs text-bone/50">No credit card</div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="register-form">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">/ get started</div>
          <h1 className="mt-4 font-marketing text-5xl tracking-tight">Get your score.</h1>

          <div className="mt-10 space-y-5">
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Business name</label>
              <input
                type="text"
                required
                data-testid="register-business-input"
                value={form.business_name}
                onChange={(e) => update("business_name", e.target.value)}
                className="mt-1 w-full bg-card border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-intl"
              />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Website URL</label>
              <input
                type="text"
                required
                data-testid="register-url-input"
                placeholder="yourbusiness.com"
                value={form.website_url}
                onChange={(e) => update("website_url", e.target.value)}
                className="mt-1 w-full bg-card border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-intl"
              />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Email</label>
              <input
                type="email"
                required
                data-testid="register-email-input"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="mt-1 w-full bg-card border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-intl"
              />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Password</label>
              <input
                type="password"
                required
                minLength={6}
                data-testid="register-password-input"
                value={form.password}
                onChange={(e) => update("password", e.target.value)}
                className="mt-1 w-full bg-card border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-intl"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="register-submit-button"
            className="mt-8 w-full py-4 bg-obsidian text-bone font-app font-bold uppercase tracking-wider hover:bg-intl transition-colors disabled:opacity-60"
          >
            {busy ? "Creating account…" : "Create account & run audit"}
          </button>

          <div className="mt-6 flex items-center gap-3" aria-hidden="true">
            <div className="h-px flex-1 bg-border" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="mt-6">
            <GoogleAuthButton label="Sign up with Google" testid="register-google-button" />
          </div>

          <div className="mt-6 text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" data-testid="goto-login-link" className="text-obsidian font-medium underline underline-offset-4">Login</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
